import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, History } from "lucide-react";
import { sharedApi } from "@/lib/api";

/** Iter 657 — "🕘 Edit history" collapsible card on the vendor dashboard.
 *  GET /classifieds/engagement/vendors/me/edit-history (X-Store-Id aware
 *  via the sharedApi interceptor). Filter chips All/Bills/Profile/Till/
 *  Sound box map to entity_type. */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

const CHIPS = [
  { key: "", label: "All" },
  { key: "bill", label: "Bills" },
  { key: "vendor_profile", label: "Profile" },
  { key: "till_settings", label: "Till" },
  { key: "sound_devices", label: "Sound box" },
];

const ACTOR_STYLE = {
  owner: "bg-sky-100 text-sky-700",
  staff: "bg-amber-100 text-amber-700",
  admin: "bg-pink-100 text-pink-700",
  system: "bg-slate-100 text-slate-500",
};

const VERB = {
  bill_entered: "entered the bill",
  bill_corrected: "corrected the bill",
  profile_updated: "edited the store profile",
  settings_changed: "changed till settings",
  sound_devices_updated: "updated the sound box",
};

function fmtVal(field, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "on" : "off";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "number" && /amount|bill|price|fee/i.test(field)) return `$${v.toFixed(2)}`;
  return String(v);
}

export default function EditHistoryBlock() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");

  const q = useQuery({
    queryKey: ["vendor-edit-history", type],
    enabled: open,
    queryFn: async () =>
      (
        await sharedApi.get("/classifieds/engagement/vendors/me/edit-history", {
          ...OPTS,
          params: { ...OPTS.params, entity_type: type || undefined, limit: 50 },
        })
      ).data,
    retry: 0,
  });

  const items = q.data?.items || [];

  return (
    <div className="rounded-2xl bg-white shadow-card p-5" data-testid="vendor-edit-history-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="vendor-edit-history-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <span className="inline-flex items-center gap-2 font-display font-bold text-slate-900">
          <History className="w-4 h-4 text-slate-400" /> 🕘 Edit history
        </span>
        <span className="text-xs font-bold text-slate-400">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-1.5">
            {CHIPS.map((c) => (
              <button
                key={c.key || "all"}
                type="button"
                onClick={() => setType(c.key)}
                data-testid={`vendor-edit-history-chip-${c.key || "all"}`}
                className={`rounded-full px-3 py-1 text-[11px] font-bold border transition-colors ${
                  type === c.key
                    ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                    : "bg-white text-slate-600 border-soft hover:border-[color:var(--brand-blue)]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {q.isLoading && (
            <div className="mt-4 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {!q.isLoading && q.isError && (
            <div className="mt-4 text-xs text-slate-500">Couldn&apos;t load the edit history.</div>
          )}
          {!q.isLoading && !q.isError && items.length === 0 && (
            <div className="mt-4 text-xs text-slate-400">No edits recorded yet.</div>
          )}

          <ul className="mt-3 space-y-3">
            {items.map((r, i) => {
              const actor = r.actor || {};
              const style = ACTOR_STYLE[actor.type] || ACTOR_STYLE.system;
              return (
                <li
                  key={r.id || i}
                  data-testid={`vendor-edit-history-row-${i}`}
                  className="rounded-xl border border-soft p-3 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${style}`}>
                      {actor.type || "system"}
                    </span>
                    <span className="font-bold text-slate-800">{actor.name || "—"}</span>
                    <span className="text-slate-600">{VERB[r.action] || r.action}</span>
                    <span className="ml-auto text-slate-400">
                      {r.at ? new Date(r.at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }) : ""}
                    </span>
                  </div>
                  {r.entity_type === "bill" && r.entity_id && (
                    <div className="mt-1 text-slate-400">Session {r.entity_id}</div>
                  )}
                  {(r.changes || []).map((c, j) => (
                    <div key={j} className="mt-1 text-slate-600">
                      <span className="font-semibold">{String(c.field || "").replace(/_/g, " ")}:</span>{" "}
                      {fmtVal(c.field, c.old ?? c.from)} → {fmtVal(c.field, c.new ?? c.to)}
                    </div>
                  ))}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
